import path from 'path';
import { mkdir, writeFile, stat } from 'fs/promises';
import { existsSync } from 'fs';
import Debug from '../../helpers/debug.js';
import ora from 'ora';
import Table from 'cli-table';
import AWS from 'aws-sdk';
import descriptions from '../../helpers/descriptions.js';
import print, { pr, pp, cl, themed, printJSON } from '../../helpers/print.js';
import { filterFiles, userSetOption, streamToString } from '../../helpers/utils.js';
import { genCredentialsOnClientApi } from '../../helpers/api.js';

const debug = Debug('commands:snapshots:download');

const command = 'download';

const desc = 'Download Snapshots';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('downloadTo', descriptions.options['downloadTo'].config)
    .demandOption('downloadTo', descriptions.options['downloadTo'].demandText)
    .option('tenant', { ...descriptions.options['tenant'].config, default: 'default' })
    .option('overwrite', descriptions.options['overwrite'].config)
    .demandOption('overwrite', descriptions.options['overwrite'].demandText);
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, tenant, downloadTo, overwrite } = argv;
  const { clientApiBaseUri } = hg;

  let message;
  let spinner = ora();
  const folderPrefix = path.resolve(process.cwd(), userSetOption('downloadTo') ? downloadTo : 'snapshots');
  try {
    // 1. get STS credentials
    message = themed(`Verifying user and authorizing`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    const { accessKeyId, secretAccessKey, sessionToken } = await genCredentialsOnClientApi({
      debug,
      baseUri: clientApiBaseUri,
      task: 'snapshot-download',
      env,
      tenant,
      apikey,
    });
    // created authenticated S3 instance
    const s3 = new AWS.S3({
      accessKeyId,
      secretAccessKey,
      sessionToken,
    });
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // 2. verify tenant is valid
    message = themed(`Validating tenant exists`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    try {
      // this may also throw access denied
      const { CommonPrefixes: tenantFolders } = await s3
        .listObjectsV2({ Bucket: env, Prefix: `${tenant}/`, Delimiter: '/' })
        .promise();
      if (tenantFolders.length === 0) throw new Error('Invalid tenant');
    } catch (err) {
      debug('err', err);
      throw new Error(`Tenant doesn't exist. Please check your tenant (-u) argument.`);
    }
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // 3. get list of downloadable files
    message = themed(`Scanning for downloadable files`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    const keyPrefix = `${tenant}/snapshots/`;
    const params = {
      Bucket: env,
      Prefix: keyPrefix,
    };
    const { Contents: s3Files } = await s3.listObjectsV2(params).promise();
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));
    debug('s3-list-res', s3Files);
    // filter unwanted files from source
    const filesToDownload = s3Files ? s3Files.map((item) => item.Key).filter((item) => item !== keyPrefix) : [];
    if (!filesToDownload || filesToDownload.length === 0) {
      json
        ? printJSON({ status: 'success', downloadedFiles: [] })
        : spinner.warn(themed(`Warning: ${themed('Nothing to download!')}`, 'secondary'));
      return;
    }

    // 4. download files
    message = themed(`Downloading snapshot files to ${themed(folderPrefix, 'info')}`);
    !json && spinner.info(themed(`Info: ${message}.`, 'secondary'));
    const table = new Table({ head: ['File', 'Status'] });
    for await (const file of filesToDownload) {
      const subPath = file.substring(keyPrefix.length);
      const fullPath = path.resolve(folderPrefix, subPath);
      debug('file', fullPath);
      if (file.endsWith('/')) {
        !existsSync(fullPath) && (await mkdir(fullPath, { recursive: true }));
        continue;
      }
      // skip if exists and overwrite is false
      if (!overwrite) {
        try {
          await stat(fullPath);
          debug('exists, skipping');
          table.push([subPath, 'Skipped']);
          continue;
        } catch (err) {
          if (err.code !== 'ENOENT') cl(err);
        }
      }

      message = themed(`Downloading file: ${themed(subPath, 'info')}`);
      !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
      // ensure destination folder exists
      await mkdir(path.dirname(fullPath), { recursive: true });
      // download s3 object into destination folder
      const fileStream = s3.getObject({ Bucket: env, Key: file }).createReadStream();
      await writeFile(fullPath, await streamToString(fileStream));
      !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

      table.push([subPath, 'Downloaded']);
    }

    if (json) {
      printJSON({ status: 'success', downloadedFiles: filesToDownload });
    } else {
      // print results
      cl(table.toString());
    }
  } catch (err) {
    if (json) {
      printJSON({ status: 'error', error: err });
    } else {
      spinner.fail(themed(`Error: ${message}.`, 'secondary'));
      pr(themed(`Message: ${themed(err.message)}`, 'secondary'));
      debug(err);
      if (err && err.response && err.response.data) {
        debug('response', err.response.data);
      }
    }
  }
};

export { command, desc, builder, handler };
